import type { EventData } from '@store/models/events/entities';
import type { BookingDraft, SpaceDetails } from '@store/models/booking';
import type { SpaceWithAvailability } from '@store/models/spaces';
import { formatUtcTimeInTimeZone, getIsoDateInTimeZone } from '@utils/timeZone';

function boolToYesNo(value: boolean | undefined): string {
  return value ? 'yes' : 'no';
}

function extractDateOnly(isoString: string, timeZone?: string): string {
  if (timeZone) {
    return getIsoDateInTimeZone(isoString, timeZone) || isoString.slice(0, 10);
  }
  return isoString.slice(0, 10);
}

function extractTime(isoString: string, timeZone?: string): string {
  if (timeZone) {
    return formatUtcTimeInTimeZone(isoString, 'en-GB', timeZone, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  }
  const date = new Date(isoString);
  const hours = date.getUTCHours().toString().padStart(2, '0');
  const minutes = date.getUTCMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
}

export function mapEventDataToFormData(
  event: EventData,
  timeZone?: string
): Record<string, unknown> {
  const formData: Record<string, unknown> = {};

  if (event.eventStartDate && event.eventEndDate) {
    formData.eventDateRange = {
      start: extractDateOnly(event.eventStartDate, timeZone),
      end: extractDateOnly(event.eventEndDate, timeZone),
    };
    formData.eventTimeRange = {
      start: extractTime(event.eventStartDate, timeZone),
      end: extractTime(event.eventEndDate, timeZone),
    };
  }

  if (event.buildingId) formData.buildingId = event.buildingId;
  if (event.numberOfAttendees !== undefined) formData.numberOfAttendees = event.numberOfAttendees;
  if (event.eventTitle !== undefined) formData.eventTitle = event.eventTitle;
  if (event.visitPurposeCode !== undefined) formData.visitPurposeCode = event.visitPurposeCode;
  if (event.businessGroupCode !== undefined) formData.businessGroupCode = event.businessGroupCode;
  if (event.businessJustification !== undefined)
    formData.businessJustification = event.businessJustification;
  if (event.attendeeTypeCode !== undefined) formData.attendeeTypeCode = event.attendeeTypeCode;

  if (event.sponsor) {
    formData['sponsor.name'] = event.sponsor.name;
    formData['sponsor.email'] = event.sponsor.email;
    formData['sponsor.costCenter'] = event.sponsor.costCenter;
  }

  if (event.hasOnSiteContact !== undefined)
    formData.hasOnSiteContact = boolToYesNo(event.hasOnSiteContact);
  if (event.onSiteContacts) formData.onSiteContacts = event.onSiteContacts;
  if (event.additionalInformation !== undefined)
    formData.additionalInformation = event.additionalInformation;

  if (event.hasCatering !== undefined) formData.hasCatering = boolToYesNo(event.hasCatering);
  if (event.filmingRequired !== undefined)
    formData.filmingRequired = boolToYesNo(event.filmingRequired);
  if (event.overnightStorageRequired !== undefined)
    formData.overnightStorageRequired = boolToYesNo(event.overnightStorageRequired);
  if (event.shipmentsExpected !== undefined)
    formData.shipmentsExpected = boolToYesNo(event.shipmentsExpected);

  if (event.spaces?.[0]?.selectedSetupCodes) {
    formData.selectedSetups = event.spaces[0].selectedSetupCodes;
  }

  // Re-set UI-only acknowledgement fields that aren't persisted in the backend.
  // consentAcknowledgement is auto-set because it's a step-level field the user already accepted.
  // policyAcknowledgement is intentionally NOT set here — on draft resume the user must
  // re-acknowledge conference room policies on the review screen before submitting.
  if (event.hasOnSiteContact) {
    formData.consentAcknowledgement = true;
  }

  return formData;
}

const STEP_ID_TO_INDEX: Record<string, number> = {
  locationDateTime: 0,
  spaceSelection: 1,
  eventDetails: 2,
  additionalDetails: 3,
  review: 4,
};

export function getStepIndex(currentStep: string | undefined): number {
  if (!currentStep) return 0;
  return STEP_ID_TO_INDEX[currentStep] ?? 0;
}

export function buildBookingDraft(
  event: EventData,
  formData: Record<string, unknown>
): BookingDraft {
  let stepIndex = getStepIndex(event.currentStep);
  // Legacy fallback for drafts saved before backend persisted currentStep:
  // if a space is selected but no currentStep is recorded, assume past step 1.
  if (!event.currentStep && event.spaces?.[0]?.spaceId && stepIndex < 2) {
    stepIndex = 2;
  }
  return {
    draftId: event.eventId as string,
    data: formData,
    currentStepIndex: stepIndex,
    highestVisitedStep: stepIndex,
    createdAt: event.createdAt,
    lastModifiedAt: event.lastUpdatedAt,
    status: 'draft',
  };
}

export function mapSpaceToDetails(space: SpaceWithAvailability): SpaceDetails {
  return {
    id: space.spaceId as string,
    name: space.spaceName,
    imageUrl: space.imageUrl,
    floor: space.floor,
    roomNumber: space.roomNumber,
    roomCapacity: space.roomCapacity,
  };
}
